import { useShopI18n } from '../i18n/ShopI18n'
import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import type { CouponResult, CouponReward } from '../services/couponApi'
import { describeCouponReward, couponRewardIconSelector, couponRewardLanguage } from '../utils/couponRewards'
import ShopDialog from './ShopDialog'
import { useAssetUrl } from '../hooks/useAsset'
import { BalanceRow } from './RewardReceipt'
import './result-ui.css'

function CouponRewardRow({ reward }: { reward: CouponReward }) {
    const { t, locale } = useShopI18n()
    const display = describeCouponReward(reward, locale, t)
    const assetUrl = useAssetUrl(couponRewardIconSelector(reward), { language: couponRewardLanguage(reward.presentation?.locale || locale) })
    const [failedUrl, setFailedUrl] = useState<string | null>(null)
    const icon = assetUrl && assetUrl !== failedUrl ? assetUrl : display.icon
    return <li>
        <img src={icon} alt="" onError={() => setFailedUrl(assetUrl)} />
        <div><span>{display.label}</span>{display.details && <small>{display.details}</small>}</div>
        <b>{display.amount}</b>
    </li>
}

export function CouponResultView({ result, onBack, onReturnToGame }: { result: CouponResult; onBack: () => void; onReturnToGame?: () => void }) {
    const { t } = useShopI18n()
    return <section className="shop-result shop-result-page shop-coupon-success" role="status">
        <div className="shop-result-sign" data-status="success" aria-hidden="true"><img src="/assets/webshop/result-checkmark.png" alt="" /></div>
        <h1>{t("Gutscheincode eingelöst!")}</h1><p>{t("Deine Belohnungen findest du im Spiel.")}</p>
        <div className="shop-result-receipt" aria-label={t("Eingelöste Belohnungen")}>
            {result.items.length > 0 ? <ul className="shop-coupon-rewards">{result.items.map((reward, index) => <CouponRewardRow key={index} reward={reward} />)}</ul> : <p>{t("Der Server hat keine einzelnen Belohnungen zurückgegeben. Prüfe die Gutschrift bitte im Spiel.")}</p>}
            <dl className="shop-receipt shop-reward-receipt"><BalanceRow balance={result.balance} /></dl>
        </div>
        {onReturnToGame && <button className="shop-price" onClick={onReturnToGame}>{t("Zurück zum Spiel")}</button>}
        <button className="shop-secondary" onClick={onBack}>{t("Weiter einkaufen")}</button>
    </section>
}

export default function CouponRedemption({ onRedeem, onClose, onRedeemed }: { onRedeemed?: (result: CouponResult) => void; onRedeem: (code: string) => Promise<CouponResult>; onClose: () => void }) {
    const { t } = useShopI18n()
    const [code, setCode] = useState('')
    const [pending, setPending] = useState(false)
    const [error, setError] = useState('')
    const [result, setResult] = useState<CouponResult | null>(null)
    const busy = useRef(false)
    const submit = async (event: FormEvent) => {
        event.preventDefault()
        if (busy.current || result) return
        if (!code.trim()) { setError(t('Bitte gib einen Gutscheincode ein.')); return }
        busy.current = true
        setPending(true)
        setError('')
        try { const redeemed = await onRedeem(code.trim()); setResult(redeemed); setCode(''); onRedeemed?.(redeemed) }
        catch (cause) { setError(cause instanceof Error ? t(cause.message) : t("Einlösen fehlgeschlagen. Bitte versuche es später erneut.")) }
        finally { busy.current = false; setPending(false) }
    }
    if (result) return onRedeemed ? null : <CouponResultView result={result} onBack={onClose} />
    return <ShopDialog title={t("Gutscheincode einlösen")} onClose={() => { if (!busy.current) onClose() }}>
        <form className="shop-coupon-form" onSubmit={submit} aria-busy={pending}>
            <label htmlFor="shop-coupon-code">{t("Gutscheincode")}</label>
            <input id="shop-coupon-code" value={code} onChange={event => setCode(event.target.value)} disabled={pending} placeholder={t("Gutscheincode eingeben")} autoComplete="off" autoCapitalize="off" spellCheck={false} aria-invalid={!!error} aria-describedby={error ? 'shop-coupon-error' : undefined} />
            {error && <p id="shop-coupon-error" role="alert">{error}</p>}
            <p>{t("Codes erhältst du in Newslettern und Aktionen.")}</p>
            <div className="shop-coupon-actions"><button className="shop-secondary" type="button" disabled={pending} onClick={onClose}>{t("Abbrechen")}</button>
                <button className="shop-price" type="submit" disabled={pending}>{pending ? t("Wird eingelöst …") : t("Einlösen")}</button>
            </div>
        </form>
    </ShopDialog>
}
